// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   src/app/auxiliares/servicios/cliente-jerarquia.service.ts
// CAMBIOS: funciones puras sobre un índice que arma quien las usa, en vez de un
//   servicio con caché propia de cinco minutos.

import type { ICliente } from 'modelos';

/** Los clientes visibles, indexados por id. */
export type IndiceClientes = ReadonlyMap<string, ICliente>;

export function indexar(clientes: readonly ICliente[]): IndiceClientes {
  return new Map(clientes.filter((c) => c._id).map((c) => [c._id!, c]));
}

/**
 * La cadena desde el ancestro más alto visible hasta el cliente, incluido.
 *
 * Hace falta porque en el listado sólo se ve el nombre y hay municipios con
 * clientes homónimos: dos "Centro" que cuelgan de intendencias distintas se ven
 * igual. Corta en cuanto encuentra un id repetido: un ciclo en `idPadre` no
 * puede colgar la pantalla.
 */
export function cadena(indice: IndiceClientes, id: string | undefined): ICliente[] {
  if (!id) return [];
  const cadena: ICliente[] = [];
  const vistos = new Set<string>();
  let actual = indice.get(id);
  while (actual?._id && !vistos.has(actual._id)) {
    vistos.add(actual._id);
    cadena.unshift(actual);
    actual = actual.idPadre ? indice.get(actual.idPadre) : undefined;
  }
  return cadena;
}

/** `Intendencia ▸ Zona norte ▸ Centro`. Vacío si no hay ancestros. */
export function ruta(indice: IndiceClientes, id: string | undefined): string {
  const partes = cadena(indice, id);
  if (partes.length < 2) return '';
  return partes.map((c) => c.nombre ?? 'Sin nombre').join(' ▸ ');
}

/** El nombre del cliente, o un aviso: la fila igual tiene que decir algo. */
export function nombreDe(indice: IndiceClientes, id: string | undefined): string {
  if (!id) return 'Sin cliente';
  return indice.get(id)?.nombre ?? 'Cliente que ya no está';
}

/**
 * Los descendientes de un cliente, él incluido.
 *
 * Se recorre hacia abajo por `idPadre` en vez de mirar `idsAncestros` porque el
 * índice ya está en memoria y `idsAncestros` puede venir sin poblar.
 */
export function descendientes(indice: IndiceClientes, id: string): Set<string> {
  const hijos = new Map<string, string[]>();
  for (const cliente of indice.values()) {
    if (!cliente.idPadre || !cliente._id) continue;
    const lista = hijos.get(cliente.idPadre) ?? [];
    lista.push(cliente._id);
    hijos.set(cliente.idPadre, lista);
  }

  const resultado = new Set<string>([id]);
  const pendientes = [id];
  while (pendientes.length) {
    const actual = pendientes.pop()!;
    for (const hijo of hijos.get(actual) ?? []) {
      if (resultado.has(hijo)) continue;
      resultado.add(hijo);
      pendientes.push(hijo);
    }
  }
  return resultado;
}
